import { MAX_COMMENT_CHARS } from "@/lib/feedback/config"
import * as m from "@/paraglide/messages"

const BYTES_PER_MB = 1024 * 1024
const DEFAULT_MAX_IMAGE_BYTES = 10 * BYTES_PER_MB

const PROVISIONING_SESSION_EXPIRED_MESSAGE =
  "La sesión de configuración del proyecto expiró. Contacta a soporte para continuar."

/**
 * One entry in Rails `record.errors.details[field]`. `error` is a stable symbol
 * (`taken`, `too_short`, `blank`, `confirmation`, `invalid`, ...) for standard
 * validations, or free-text prose / `custom` for `errors.add(:base, "...")`.
 * Option metadata (`count`, `value`, ...) sits at the TOP LEVEL alongside
 * `error`, not nested under `options`.
 */
export interface FieldErrorDetail {
  error: string
  count?: number
  value?: unknown
  [option: string]: unknown
}

/** field name → array of details. Mirrors Rails `record.errors.details`. */
export type FieldErrorMap = Record<string, FieldErrorDetail[]>

/** Normalized view of any backend error body, ready for translation. */
export interface ParsedApiError {
  /**
   * Top-level `error` symbol from the API (e.g. "unprocessable_entity",
   * "forbidden", "not_found", "bad_request"). For 401 the API sends an English
   * sentence here, so the mapper switches on `status`, not this. Undefined when
   * absent.
   */
  code?: string
  /** Backend parameter/field identifier for a 400 validation failure. */
  param?: string
  /** Per-field validation details (from `error_codes`). Empty when none. */
  fieldErrors: FieldErrorMap
  /** Backend human messages (English). Last-resort signal — never shown directly. */
  messages: string[]
  status: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function coerceStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : []
}

function coerceFieldErrors(value: unknown): FieldErrorMap {
  if (!isRecord(value)) return {}
  const result: FieldErrorMap = {}
  for (const [field, details] of Object.entries(value)) {
    if (!Array.isArray(details)) continue
    const parsed = details.filter(
      (detail): detail is FieldErrorDetail =>
        isRecord(detail) && typeof detail.error === "string",
    )
    if (parsed.length > 0) result[field] = parsed
  }
  return result
}

/**
 * Normalize any backend error body into a {@link ParsedApiError}. Tolerant of
 * every shape the API (and the Next route handlers that relay it) can produce:
 * the raw Rails 422 (`error` + `error_codes` + `error_messages`), the
 * route-handler-relayed `{ errors: [...] }`, and the `{ message }` of 400/403.
 */
export function parseApiError(data: unknown, status: number): ParsedApiError {
  if (!isRecord(data)) {
    return { code: undefined, param: undefined, fieldErrors: {}, messages: [], status }
  }

  // Rails' raw shape names these `error` / `error_codes`. A route handler that
  // relays an ApiError it already parsed once (see lib/api/client.ts) instead
  // forwards `code` / `fieldErrors` — its own property names — alongside
  // `errors` for backward compatibility. Accept either spelling so a second
  // parse on the browser side (via clientApiFetch) recovers the same
  // code/fieldErrors the route handler saw from Rails.
  const code =
    typeof data.error === "string"
      ? data.error
      : typeof data.code === "string"
        ? data.code
        : undefined
  const param = typeof data.param === "string" ? data.param : undefined
  let fieldErrors = coerceFieldErrors(data.error_codes)
  if (Object.keys(fieldErrors).length === 0) {
    fieldErrors = coerceFieldErrors(data.fieldErrors)
  }

  // Rails 422 → error_messages; route-handler-relayed shape → errors; 400/403 → message.
  let messages = coerceStringArray(data.error_messages)
  if (messages.length === 0) messages = coerceStringArray(data.errors)
  if (messages.length === 0 && typeof data.message === "string") {
    messages = [data.message]
  }

  return { code, param, fieldErrors, messages, status }
}

function detailCount(detail: FieldErrorDetail): number | undefined {
  if (typeof detail.count === "number") return detail.count
  // Defensive: tolerate a nested `options.count` shape too.
  const options = detail.options
  if (isRecord(options) && typeof options.count === "number") return options.count
  return undefined
}

/** Tier 1a — exact (field, symbol) match. */
function mapFieldSymbol(
  field: string,
  symbol: string,
  detail: FieldErrorDetail,
): string | undefined {
  switch (`${field}:${symbol}`) {
    case "email:taken":
      return m.error_email_taken()
    case "email:invalid":
      return m.error_email_invalid()
    case "password:too_short":
      return m.error_password_too_short({ count: detailCount(detail) ?? 6 })
    case "password_confirmation:confirmation":
      return m.error_password_confirmation()
    case "reset_password_token:invalid":
      return m.password_reset_token_invalid()
    case "phone:invalid":
      return m.error_phone_invalid()
    case "taxpayer_id:taken":
      return m.error_taxpayer_id_taken()
    case "taxpayer_id:invalid":
      return m.error_taxpayer_id_invalid()

    // Passkey / plan-selection / credential-reveal validations (C2 contract:
    // 403 → 422 with a stable per-field code). `record_type:invalid` was
    // deliberately left off this list under the C2 contract (unreachable from
    // that UI) — it now has its own case below (I4), added for
    // Records::CreateService, whose record_type IS user-reachable.
    case "passkey_secret:invalid":
      return m.error_passkey_secret_invalid()
    case "passkey_secret:revoked":
      return m.error_passkey_secret_revoked()
    case "passkey_secret:expired":
      return m.error_passkey_secret_expired()
    case "passkey:disabled":
      return m.error_passkey_disabled()
    case "passkey:inactive_program":
      return m.error_passkey_inactive_program()
    case "passkey:inactive_partner":
      return m.error_passkey_inactive_partner()
    case "provisioning_partner_code:not_applicable_for_plan":
      return m.error_provisioning_partner_code_not_applicable_for_plan()
    case "plan:unsupported":
      return m.error_plan_unsupported()
    case "passkey:required_for_partner_plan":
      return m.error_passkey_required_for_partner_plan()
    case "provisioning_partner_code:required_for_master_passkey":
      return m.error_provisioning_partner_code_required_for_master_passkey()
    case "provisioning_partner_code:unavailable":
      return m.error_provisioning_partner_code_unavailable()
    case "provisioning_partner_code:not_applicable_for_passkey":
      return m.error_provisioning_partner_code_not_applicable_for_passkey()
    case "passkey:not_allowed_for_insurer":
      return m.error_passkey_not_allowed_for_insurer()
    case "passkey:required_for_platform_plan":
      return m.error_passkey_required_for_platform_plan()
    case "passkey:partner_mismatch":
      return m.error_passkey_partner_mismatch()
    case "credential:unavailable":
      return m.error_credential_unavailable()
    case "credential:not_recoverable":
      return m.error_credential_not_recoverable()

    case "context:invalid_preview_context":
      return m.export_preview_unavailable()

    // Records (Records::CreateService / UpdateService, records_controller).
    case "tag_standar:required":
      return m.error_tag_standar_required()
    case "tag_standar:invalid":
      return m.error_tag_standar_invalid()
    // `record_type:invalid` / `remove_attachment_ids:invalid` are the codes the
    // API sent before it renamed them (standar-api, services C8). Both
    // spellings map to the same message; the old cases stay until the renamed
    // API codes are in production, and can then be removed.
    case "record_type:invalid":
    case "record_type:unknown_record_type":
      return m.error_record_type_invalid()
    case "remove_attachment_ids:invalid":
    case "remove_attachment_ids:unknown_attachment":
      return m.error_remove_attachment_ids_invalid()
    case "expected_updated_at:invalid":
      return m.error_expected_updated_at_invalid()
    // Incidence criticality (STA-192): an unknown level on update is
    // `criticality:inclusion`. On create the API reports any invalid incidence
    // as `recordable:invalid`, which the generic `invalid` mapping covers (the
    // form requires a level client-side, so that path is only a backstop).
    case "criticality:inclusion":
      return m.error_criticality_invalid()

    // Members (Companies::Members::UpdateService / DestroyService). The
    // project_ids codes are also raised by Invitations::CreateService.
    case "base:project_suspended":
      return m.error_project_member_suspended()
    case "base:last_admin":
      return m.error_member_last_admin()
    case "project_ids:foreign_project":
      return m.error_project_ids_foreign_project()
    case "project_ids:suspended_project":
      return m.error_project_ids_suspended_project()

    // Invitations (create) — Invitations::CreateService.
    case "email:already_member":
      return m.error_email_already_member()
    case "email:insurer_user":
      return m.error_email_insurer_user()
    case "project_ids:insurer_company":
      return m.error_project_ids_insurer_company()

    // Invitations (accept / signup) — Invitations::AcceptService and the
    // CompanyUser model. already_accepted/revoked/expired/mismatch reuse the
    // invitation-flow keys already mapped from prose (Tier 1c) above.
    case "base:already_accepted":
      return m.error_invitation_already_accepted()
    case "base:revoked":
      return m.error_invitation_revoked()
    case "base:expired":
      return m.error_invitation_expired()
    case "email:mismatch":
      return m.error_email_invitation_mismatch()
    case "email:constructor_user":
    case "user:constructor_user":
      return m.error_constructor_user()
    case "base:suspended_project":
      return m.error_invitation_suspended_project()

    // Forms review (app/services/forms/*).
    case "review_status:not_pending_review":
      return m.error_review_status_not_pending_review()
    case "review_status:not_editable":
      return m.error_review_status_not_editable()
    case "review_status:not_submittable":
      return m.error_review_status_not_submittable()
    case "review_status:not_resolvable":
      return m.error_review_status_not_resolvable()
    case "review_status:not_corrective_draft":
      return m.error_review_status_not_corrective_draft()
    case "review_status:not_changes_requested":
      return m.error_review_status_not_changes_requested()
    case "base:observations_present":
      return m.error_base_observations_present()
    case "base:no_observations":
      return m.error_base_no_observations()
    case "base:unresolved_observations":
      return m.error_base_unresolved_observations()
    case "observation:not_draft":
      return m.error_observation_not_draft()
    case "observation_ids:not_pending":
      return m.error_observation_ids_not_pending()
    case "generation_status:not_reprocessable":
      return m.error_generation_status_not_reprocessable()
    // field_id also travels at the top level of these details, but a generic
    // message is fine here — resolving it to a field label is out of scope.
    case "responses:required":
      return m.error_responses_required()
    case "responses:invalid_value":
      return m.error_responses_invalid_value()
    case "responses:attachment_not_editable":
      return m.error_responses_attachment_not_editable()
    case "signature:blank":
      return m.error_signature_blank()
    // `section_id:invalid` / `field_id:invalid` stay until the renamed API
    // codes (standar-api, services C8) are in production, then can be removed.
    case "section_id:invalid":
    case "section_id:unknown_section":
      return m.error_section_id_invalid()
    case "field_id:invalid":
    case "field_id:not_attachment_field":
      return m.error_field_id_invalid()
    case "signed_id:invalid":
      return m.error_signed_id_invalid()

    // Answer feedback (AnswerFeedbacks::UpsertService).
    case "comment:required_for_other":
      return m.answer_feedback_error_comment_required()
    case "comment:too_long":
      return m.answer_feedback_error_comment_too_long({ count: detailCount(detail) ?? MAX_COMMENT_CHARS })
    case "base:not_agent_authored":
      return m.answer_feedback_error_not_agent()
    case "reasons:invalid":
    case "reasons:not_allowed_for_up":
    case "comment:not_allowed_for_up":
      return m.answer_feedback_error_invalid()

    // Reports (app/services/reports/*).
    case "status:finalized":
      return m.error_report_status_finalized()
    case "status:not_completed":
      return m.error_report_status_not_completed()
    case "status:processing":
      return m.error_report_status_processing()

    // Findings (Findings::AcknowledgeService).
    case "status:not_open":
      return m.error_finding_status_not_open()
    case "record_type:not_acknowledgeable":
      return m.error_finding_record_type_not_acknowledgeable()
    case "base:not_a_finding":
      return m.error_finding_not_a_finding()

    // Report section images (Reports::CreateImageService).
    case "image:image_type_not_allowed":
      return m.error_report_image_type_not_allowed()
    case "image:image_too_large":
      return m.error_report_image_too_large({
        maxMb: Math.round((typeof detail.max_bytes === "number" ? detail.max_bytes : DEFAULT_MAX_IMAGE_BYTES) / BYTES_PER_MB),
      })

    // Project documents (POST /documents).
    case "file:file_not_in_project":
      return m.error_document_file_not_in_project()
    case "file:upload_missing":
      return m.projects_documents_error_server_upload_missing()
    case "kind:replacement_target_mismatch":
      return m.error_document_replacement_target_mismatch()
    case "kind:replacement_in_progress":
      return m.error_document_replacement_in_progress()

    // Notification preferences (PATCH /notification_preferences).
    case "category:not_applicable":
      return m.error_notification_category_not_applicable()
    case "channel:not_supported":
    case "delivery_mode:not_supported":
      return m.error_notification_option_not_supported()
    default:
      return undefined
  }
}

/** Tier 1b — field-agnostic symbol match. */
function mapSymbol(symbol: string, detail: FieldErrorDetail): string | undefined {
  switch (symbol) {
    case "blank":
      return m.error_field_blank()
    case "taken":
      return m.error_field_taken()
    case "invalid":
    case "inclusion":
      return m.error_field_invalid()
    case "confirmation":
      return m.error_password_confirmation()
    case "too_short": {
      const count = detailCount(detail)
      return count !== undefined
        ? m.error_field_too_short({ count })
        : m.error_field_invalid()
    }
    case "too_long": {
      const count = detailCount(detail)
      return count !== undefined
        ? m.error_field_too_long({ count })
        : m.error_field_invalid()
    }
    default:
      return undefined
  }
}

/**
 * Tier 1c — recover the few known service-level (`base`/`custom`) errors from
 * their English prose, which carries no stable symbol. Phrases are scoped to
 * the invitation flow to avoid false positives on unrelated messages.
 */
function mapProse(text: string): string | undefined {
  const t = text.toLowerCase()
  if (t.includes("already accepted")) return m.error_invitation_already_accepted()
  if (t.includes("match the invitation") || (t.includes("does not match") && t.includes("invit")))
    return m.error_email_invitation_mismatch()
  if (t.includes("invit") && t.includes("revoked")) return m.error_invitation_revoked()
  if (t.includes("invit") && t.includes("expired")) return m.error_invitation_expired()
  return undefined
}

function mapFieldDetail(field: string, detail: FieldErrorDetail): string | undefined {
  return (
    mapFieldSymbol(field, detail.error, detail) ??
    mapSymbol(detail.error, detail) ??
    mapProse(detail.error)
  )
}

/**
 * Tier-1-only: per-field validation messages, with no status/code/generic
 * fallback. Empty when `fieldErrors` is empty or maps to nothing known.
 * Callers that want a component-specific fallback (rather than the shared
 * generic one baked into {@link toSpanishMessages}) use this directly — e.g.
 * a passkey-validation form that falls back to "La llave no es válida"
 * instead of "Ocurrió un error. Intenta de nuevo." when nothing maps.
 */
export function fieldSpecificMessages(fieldErrors: FieldErrorMap): string[] {
  const lines: string[] = []
  for (const [field, details] of Object.entries(fieldErrors)) {
    for (const detail of details) {
      const mapped = mapFieldDetail(field, detail)
      if (mapped) lines.push(mapped)
    }
  }
  return [...new Set(lines)]
}

/** Tier 2 — top-level HTTP status / code. Status wins (reliable for 401). */
function mapStatusOrCode(status: number, code?: string): string | undefined {
  if (status === 401) return m.error_unauthorized()
  if (code === "provisioning_session_expired") return PROVISIONING_SESSION_EXPIRED_MESSAGE
  if (code === "request_blocked") return m.error_request_blocked()
  if (status === 403 || code === "forbidden") return m.error_forbidden()
  if (status === 404 || code === "not_found") return m.error_not_found()
  if (status === 409 || code === "conflict") return m.error_conflict()
  if (status === 400 || code === "bad_request") return m.error_bad_request()
  if (status >= 500) return m.error_server()
  return undefined
}

/**
 * Turn a parsed backend error into friendly Spanish messages via the fallback
 * ladder: per-field validation → known service-level prose → status/code →
 * generic. Always returns at least one string; never the raw code or English.
 * Returns an array so the form banner can list multiple field errors.
 */
export function toSpanishMessages(parsed: ParsedApiError): string[] {
  const { fieldErrors, messages, code, status } = parsed

  // Tier 1 — per-field validation errors.
  const lines: string[] = [...fieldSpecificMessages(fieldErrors)]

  // Tier 1c — service-level errors whose prose lands in `error_messages`.
  for (const message of messages) {
    const mapped = mapProse(message)
    if (mapped) lines.push(mapped)
  }

  const deduped = [...new Set(lines)]
  if (deduped.length > 0) return deduped

  // Tier 2 — top-level status / code.
  const byStatus = mapStatusOrCode(status, code)
  if (byStatus) return [byStatus]

  // Tier 3 — final fallback.
  return [m.error_generic()]
}
